import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaClock, 
  FaCalendarAlt, 
  FaUsers, 
  FaStar,
  FaCheckCircle,
  FaBookOpen,
  FaCertificate,
  FaArrowRight,
  FaSearch,
  FaClipboardCheck,
  FaChartLine,
  FaFileSignature,
  FaGavel,
  FaShieldAlt,
  FaBalanceScale,
  FaExclamationTriangle
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 200px;
  min-height: calc(100vh - 210px);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0a4d68 0%, #088395 50%, #0a2b3c 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  span {
    color: #ffd966;
    display: block;
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.95;
  position: relative;
  z-index: 2;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 5%;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #0a2b3c;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: #ffd966;
    border-radius: 2px;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Grid3Col = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ContentBlock = styled.div`
  p {
    color: #4a5f73;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const InfoCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(10, 77, 104, 0.1);
    border-color: #ffd966;
  }
`;


const IconWrapper = styled.div`
  font-size: 2.5rem;
  color: #ffd966;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  color: #0a2b3c;
  font-size: 1.3rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const CardText = styled.p`
  color: #5a6b7a;
  line-height: 1.6;
  font-size: 0.95rem;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  color: #ffff;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #ffd966;
    font-size: 1rem;
  }
`;



const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .value {
    font-size: 2.2rem;
    font-weight: 700;
    color: #0a4d68;
    margin-bottom: 0.3rem;
  }
  
  .label {
    color: #5a6b7a;
    font-size: 0.9rem;
    text-transform: uppercase;
  }
`;

const CertificationBox = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 2.5rem;
  color: white;
  margin: 3rem 0;
  
  h3 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1.5rem;
    opacity: 0.9;
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: white;
  margin-top: 4rem;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  background: #ffd966;
  color: #0a4d68;
  border: none;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const Auditing = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  
  const modules = [
    {
      title: "Audit Fundamentals",
      topics: [
        "Types of audits (pre-bill, post-bill, prospective, retrospective)",
        "Audit scope and methodology",
        "Sampling techniques",
        "Developing audit tools"
      ]
    },
    {
      title: "Regulatory Compliance",
      topics: [
        "OIG Work Plan",
        "Corporate Integrity Agreements",
        "False Claims Act",
        "Stark Law and Anti-Kickback Statute"
      ]
    },
    {
      title: "Coding & Documentation Audits",
      topics: [
        "E&M auditing (1995 vs 1997 guidelines)",
        "Surgical package auditing",
        "ICD-10-CM/PCS validation",
        "DRG validation"
      ]
    },
    {
      title: "Audit Reporting & Education",
      topics: [
        "Writing audit findings",
        "Presenting results to providers",
        "Corrective action plans",
        "Tracking and trending"
      ]
    }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          Medical Auditing Training
          <span>Become a Certified Professional Medical Auditor (CPMA)</span>
        </HeroTitle>
        <HeroDescription>
          Master the art and science of medical auditing. Learn to identify compliance risks, 
          improve documentation, and protect healthcare organizations from fraud and abuse.
        </HeroDescription>
      </HeroSection>

      <Container>
       

        <Grid2Col>
          <ContentBlock>
            <SectionTitle>Why Medical Auditing?</SectionTitle>
            <p>
              Healthcare compliance is more critical than ever. With increasing regulatory scrutiny 
              and complex coding guidelines, healthcare organizations need skilled auditors to 
              identify risks, ensure compliance, and prevent fraud.
            </p>
            <p>
              Our comprehensive auditing program prepares you for the CPMA (Certified Professional 
              Medical Auditor) certification and equips you with the skills to conduct thorough, 
              effective audits in any healthcare setting.
            </p>
          </ContentBlock>
          
          <InfoCard>
            <IconWrapper><FaBalanceScale /></IconWrapper>
            <CardTitle>Auditor Career Outlook</CardTitle>
            <List class ="auditing">
            
              <ListItem><FaCheckCircle /> Average salary: $75,000 - $95,000</ListItem>
              <ListItem><FaCheckCircle /> High demand nationwide</ListItem>
              <ListItem><FaCheckCircle /> Work in hospitals, clinics, consulting firms</ListItem>
              <ListItem><FaCheckCircle /> Remote work opportunities</ListItem>
              <ListItem><FaCheckCircle /> Career advancement to compliance roles</ListItem>
            </List>
          </InfoCard>
        </Grid2Col>

        <CertificationBox>
          <h3>CPMA Certification Preparation</h3>
          <p>
            This program fully prepares you for the AAPC CPMA (Certified Professional Medical Auditor) 
            examination. Our curriculum covers all exam domains with extensive practice exercises 
            and mock exams.
          </p>
          <List>
            <ListItem><FaCheckCircle /> 100% exam content coverage</ListItem>
            <ListItem><FaCheckCircle /> Practice exams with 500+ questions</ListItem>
            <ListItem><FaCheckCircle /> Case studies and real audit scenarios</ListItem>
            <ListItem><FaCheckCircle /> Exam-taking strategies</ListItem>
          </List>
        </CertificationBox>

        <SectionTitle>Course Modules</SectionTitle>
        <Grid3Col>
          {modules.map((module, index) => (
            <InfoCard key={index}>
              <IconWrapper><FaClipboardCheck /></IconWrapper>
              <CardTitle>{module.title}</CardTitle>
              <List>
                {module.topics.map((topic, idx) => (
                  <ListItem key={idx}>
                    <FaCheckCircle /> {topic}
                  </ListItem>
                ))}
              </List>
            </InfoCard>
          ))}
        </Grid3Col>

        <CTASection>
          <h3>Ready to Become an Auditor?</h3>
          <p>Join our next batch and become a certified medical auditor</p>
          <CTAButton to="/register">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>
      </Container>
    </PageContainer>
  );
};

export default Auditing;